import apiClient from '../core';
import type {
  SearchPageTemplatesRequest,
  PageTemplateBrowseResponse,
  PageTemplate,
  UpdatePageTemplateRequest,
  SiteTemplate,
  UpdateSiteTemplateRequest,
  SearchSiteTemplatesRequest,
  SiteTemplateBrowseResponse,
} from '../../schemas/validation';

// Page Templates API
export const templatesApi = {
  // Browse page templates
  async browseTemplates(params?: SearchPageTemplatesRequest): Promise<PageTemplateBrowseResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.tags) queryParams.append('tags', params.tags);
    if (params?.siteId) queryParams.append('siteId', params.siteId);
    if (params?.includePublic !== undefined)
      queryParams.append('includePublic', params.includePublic.toString());

    const url = queryParams.toString()
      ? `/pages/templates?${queryParams.toString()}`
      : '/pages/templates';
    const response = await apiClient.get<PageTemplateBrowseResponse>(url);
    return response.data;
  },

  // Get specific page template
  async getTemplate(templateId: string): Promise<PageTemplate> {
    const response = await apiClient.get<PageTemplate>(`/pages/templates/${templateId}`);
    return response.data;
  },

  // Update page template metadata
  async updateTemplate(
    templateId: string,
    data: UpdatePageTemplateRequest
  ): Promise<{
    message: string;
    template: {
      id: string;
      name: string;
      description?: string;
      tags?: string;
      isPublic: boolean;
      usageCount: number;
      updatedAt: string;
    };
  }> {
    const response = await apiClient.put<{
      message: string;
      template: {
        id: string;
        name: string;
        description?: string;
        tags?: string;
        isPublic: boolean;
        usageCount: number;
        updatedAt: string;
      };
    }>(`/pages/templates/${templateId}`, data);
    return response.data;
  },

  // Delete page template
  async deleteTemplate(templateId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/pages/templates/${templateId}`);
    return response.data;
  },

  // Search templates by type/tags
  async searchTemplates(search: string, siteId?: string): Promise<PageTemplateBrowseResponse> {
    return this.browseTemplates({ search, siteId, includePublic: true });
  },

  // Get templates by tags
  async getTemplatesByTags(tags: string, siteId?: string): Promise<PageTemplateBrowseResponse> {
    return this.browseTemplates({ tags, siteId, includePublic: true });
  },
};

// Site Templates API
export const siteTemplatesApi = {
  // Browse site templates
  async browseTemplates(params?: SearchSiteTemplatesRequest): Promise<SiteTemplateBrowseResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.tags) queryParams.append('tags', params.tags);
    if (params?.includePublic !== undefined)
      queryParams.append('includePublic', params.includePublic.toString());

    const url = queryParams.toString()
      ? `/sites/templates?${queryParams.toString()}`
      : '/sites/templates';
    const response = await apiClient.get<SiteTemplateBrowseResponse>(url);
    return response.data;
  },

  async browsePrivateTemplates(): Promise<SiteTemplateBrowseResponse> {
    const response = await apiClient.get<SiteTemplateBrowseResponse>('/sites/templates/private');
    return response.data;
  },

  // Get specific site template
  async getTemplate(templateId: string): Promise<SiteTemplate> {
    const response = await apiClient.get<SiteTemplate>(`/sites/templates/${templateId}`);
    return response.data;
  },

  // Update site template
  async updateTemplate(
    templateId: string,
    data: UpdateSiteTemplateRequest
  ): Promise<{
    message: string;
    template: {
      id: string;
      name: string;
      description?: string;
      tags?: string;
      isPublic: boolean;
      isSystem?: boolean;
      usageCount: number;
      updatedAt: string;
    };
  }> {
    const response = await apiClient.put<{
      message: string;
      template: {
        id: string;
        name: string;
        description?: string;
        tags?: string;
        isPublic: boolean;
        isSystem?: boolean;
        usageCount: number;
        updatedAt: string;
      };
    }>(`/sites/templates/${templateId}`, data);
    return response.data;
  },

  // Delete site template
  async deleteTemplate(templateId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/sites/templates/${templateId}`);
    return response.data;
  },

  // Search templates by search term
  async searchTemplates(search: string): Promise<SiteTemplateBrowseResponse> {
    return this.browseTemplates({ search, includePublic: true });
  },

  // Get templates by tags
  async getTemplatesByTags(tags: string): Promise<SiteTemplateBrowseResponse> {
    return this.browseTemplates({ tags, includePublic: true });
  },

  // Preview site template - get page content for preview
  async previewTemplate(
    templateId: string,
    pageIndex?: number
  ): Promise<{
    success: boolean;
    preview: boolean;
    template: {
      id: string;
      name: string;
      theme: string;
    };
    page: {
      index: number;
      title: string;
      slug: string;
      components: any[];
      showLayout?: boolean;
    };
    layout?: {
      id: string;
      name: string;
      components: any[] | null;
      isDefault: boolean;
    } | null;
    totalPages: number;
    totalLayouts?: number;
    allPages: Array<{ index: number; title: string; slug: string }>;
  }> {
    const url =
      pageIndex !== undefined
        ? `/sites/templates/${templateId}/preview?page=${pageIndex}`
        : `/sites/templates/${templateId}/preview`;
    const response = await apiClient.get(url);
    return response.data;
  },
};
